import * as Sentry from '@sentry/react';
import { config } from '@/config';

// Workspace tag injected into every Sentry event via beforeSend.
// Updated on login/tenant-switch, cleared on logout.
let _workspaceId: string | null = null;

export function setSentryUser(user: { id: string; email: string } | null): void {
  Sentry.setUser(user ? { id: user.id, email: user.email } : null);
}

export function setSentryWorkspace(workspaceId: string | null): void {
  _workspaceId = workspaceId;
}

export function initSentry(): void {
  if (!config.sentryDsn) return;

  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment,
    release: config.sentryRelease || undefined,
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    initialScope: {
      // pr_id lets one shared "pr-environments" Sentry environment be
      // filtered per PR (tag sub-filter) instead of an env per PR.
      tags: { service: 'web', ...(config.prId && { pr_id: config.prId }) },
    },
    // Suppress known-noisy browser/extension errors that are never actionable.
    ignoreErrors: [
      // Lazy-loaded chunk failed — user network hiccup, page refresh fixes it.
      'ChunkLoadError',
      /Loading chunk \d+ failed/,
      /Loading CSS chunk \d+ failed/,
      // Browser-internal layout noise — not caused by our code.
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Intentional fetch cancellation (AbortController / React Query cleanup).
      'AbortError',
      /The user aborted a request/,
      // Generic offline (Firefox) — already handled as ApiError OFFLINE in our stack.
      'NetworkError when attempting to fetch resource',
      // Non-Error thrown values (e.g. `throw "string"`) — not actionable.
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      // Chrome extension messaging noise: thrown into page context as an unhandled
      // promise rejection by Chrome's runtime when an extension listener returns true
      // (async) but its message channel closes before sendResponse fires. Originates
      // from the browser/extension — not our code — so denyUrls doesn't catch it.
      /A listener indicated an asynchronous response by returning true/,
    ],
    // Block errors injected by browser extensions — they come from foreign origins
    // and have nothing to do with our code.
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],
    beforeSend(event) {
      if (_workspaceId) {
        event.tags = { ...event.tags, workspaceId: _workspaceId };
      }
      return event;
    },
  });

  Sentry.getGlobalScope().setAttributes({ service: 'web' });

  Sentry.logger.info('Nexus web client ready', {
    environment: config.environment,
  });
}

export { Sentry };
